import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/toast";

interface VideoUploadFieldProps {
  id: string;
  endpoint: "/admin-upload/intro-video" | "/admin-upload/group-video";
  value: string;
  onChange: (next: string) => void;
  accept?: string;
  placeholder?: string;
  className?: string;
}

export function VideoUploadField({
  id,
  endpoint,
  value,
  onChange,
  accept = "video/*",
  placeholder = "https://...",
  className,
}: VideoUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const toast = useToast();

  const triggerPick = () => inputRef.current?.click();

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const url = await uploadVideo({ file, endpoint, onProgress: setProgress });
      onChange(url);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error("Upload failed", err instanceof Error ? err.message : undefined);
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex gap-2">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <Button
          type="button"
          variant="outline"
          onClick={triggerPick}
          disabled={uploading}
          className="shrink-0"
        >
          <Upload className="h-4 w-4" />
          {uploading ? "Uploading..." : "Upload"}
        </Button>
        {value && !uploading && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange("")}
            aria-label="Clear video URL"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {uploading && progress !== null && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-surface-muted">
          <div
            className="h-full bg-accent transition-[width] duration-150"
            style={{ width: `${Math.min(100, Math.round(progress * 100))}%` }}
          />
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onPick}
      />
    </div>
  );
}

async function uploadVideo({
  file,
  endpoint,
  onProgress,
}: {
  file: File;
  endpoint: string;
  onProgress?: (fraction: number) => void;
}): Promise<string> {
  // XHR for upload progress (fetch doesn't expose it).
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", endpoint, true);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.setRequestHeader("x-file-name", file.name);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };
    xhr.onload = () => {
      const payload = safeJsonParse(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300 && payload?.url) {
        resolve(String(payload.url));
        return;
      }
      const message =
        typeof payload?.error === "string" ? payload.error : `Upload failed (${xhr.status})`;
      reject(new Error(message));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.ontimeout = () => reject(new Error("Upload timed out"));
    xhr.timeout = 10 * 60 * 1000;
    xhr.send(file);
  });
}

function safeJsonParse(value: string): Record<string, unknown> | null {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
